/**
 * Popover, vendored from the React Native Reusables registry.
 *
 * Adapted only where this repo's kit differs from the registry: the imports
 * point at the shims in `src/shims` rather than `react-native-reanimated` and
 * `react-native-screens`, and the two Tailwind v4 classes in the upstream
 * source (`outline-hidden`, `origin-(--radix-popover-content-transform-origin)`)
 * are written in this repo's Tailwind v3 syntax.
 *
 * The content carries a `mr-4` gutter, which is not the registry's. Radix moves
 * a popover only as far as it must to keep it on screen, so a panel that would
 * overflow lands exactly on the window edge — measured at 700, 900 and 1194
 * pixels wide, the date picker's right edge sat at the viewport edge to the
 * pixel. Radix's own knob for this is `collisionPadding`, and it cannot be
 * reached from here: @rn-primitives passes a fixed set of props to Radix's
 * content and spreads the rest onto the view inside it, so the margin is what
 * gets through. It works because that view shrink-wraps this content, which puts
 * the gutter inside the box Radix keeps on screen — and because every popover in
 * the kit is start-aligned, where a right gutter cannot move the panel's own
 * left edge.
 *
 * It is here for the date-time picker: RNR ships no calendar, so the popover is
 * the anchor the picker hangs from.
 */

import { NativeOnlyAnimatedView } from './native-only-animated-view';
import { TextClassContext } from './text';
import { cn } from '../../lib/utils';
import * as PopoverPrimitive from '@rn-primitives/popover';
import * as React from 'react';
import { Platform, StyleSheet } from 'react-native';
import { FadeIn, FadeOut, ReduceMotion } from '../../shims/reanimated';
import { FullWindowOverlay as RNFullWindowOverlay } from '../../shims/screens';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

/** Closes the popover from inside its content; the Done button uses it. */
const PopoverClose = PopoverPrimitive.Close;

const FullWindowOverlay = Platform.OS === 'ios' ? RNFullWindowOverlay : React.Fragment;

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  portalHost,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & {
  portalHost?: string;
}) {
  return (
    <PopoverPrimitive.Portal hostName={portalHost}>
      <FullWindowOverlay>
        <PopoverPrimitive.Overlay
          style={Platform.select({ native: StyleSheet.absoluteFill })}
          asChild={Platform.OS !== 'web'}>
          <NativeOnlyAnimatedView
            entering={FadeIn.duration(200).reduceMotion(ReduceMotion.System)}
            exiting={FadeOut.reduceMotion(ReduceMotion.System)}
            as="Pressable">
            <TextClassContext.Provider value="text-popover-foreground">
              <PopoverPrimitive.Content
                align={align}
                sideOffset={sideOffset}
                className={cn(
                  'bg-popover border-border z-50 mr-4 w-72 rounded-md border p-4 shadow-md shadow-black/5 outline-none',
                  Platform.select({
                    web: cn(
                      'animate-in fade-in-0 zoom-in-95 origin-[var(--radix-popover-content-transform-origin)] cursor-auto',
                      props.side === 'bottom' && 'slide-in-from-top-2',
                      props.side === 'top' && 'slide-in-from-bottom-2'
                    ),
                  }),
                  className
                )}
                {...props}
              />
            </TextClassContext.Provider>
          </NativeOnlyAnimatedView>
        </PopoverPrimitive.Overlay>
      </FullWindowOverlay>
    </PopoverPrimitive.Portal>
  );
}

export { Popover, PopoverClose, PopoverContent, PopoverTrigger };
